<template>
 <a-row class="mb-5">
  <a-col :xs="24" class="mb-2">
   <label>Woninggegevens</label>
   <a-spin v-if="loading" size="small" style="margin-left: 10px"/>
  </a-col>
  <a-col :xs="24" class="mb-2">
   <small>Nodig voor ISDE en gemeentelijke subsidie. Velden met "BAG" zijn automatisch ingevuld en kunnen worden aangepast;
    zonder antwoord tonen we de subsidie als indicatief.</small>
  </a-col>
  <a-col v-if="required" :xs="24" class="mb-2">
   <a-alert message="Bij isolatie zijn alle woninggegevens (*) verplicht: ze bepalen de ISDE en gemeentelijke subsidie." show-icon type="info"/>
  </a-col>
  <a-col v-if="bagStatus === 'down'" :xs="24" class="mb-2">
   <a-alert message="De BAG is nu niet bereikbaar. Vul de woninggegevens handmatig in." show-icon type="warning">
    <template #action>
     <a-button :loading="loading" size="small" @click="retry">Opnieuw proberen</a-button>
    </template>
   </a-alert>
  </a-col>
  <a-col v-else-if="bagStatus === 'partial'" :xs="24" class="mb-2">
   <a-alert message="Niet alles gevonden in de BAG. Vul de lege velden handmatig in." show-icon type="info"/>
  </a-col>
  <a-col :xs="24">
   <a-form v-if="building" ref="form" :model="building" :rules="required ? rules : {}" layout="vertical">
   <a-row :gutter="30">
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item name="buildingYear">
      <a-input v-model:value="building.buildingYear" :addon-after="fromBag.buildingYear ? 'BAG' : null"
               :addon-before="mark('Bouwjaar')" type="number"/>
     </a-form-item>
    </a-col>
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item name="municipality">
      <a-input v-model:value="building.municipality" :addon-after="fromBag.municipality ? 'BAG' : null"
               :addon-before="mark('Gemeente')"/>
     </a-form-item>
    </a-col>
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item :help="fromBag.isGroundLevel ? 'Uit BAG' : undefined" label="Grondgebonden woning" name="isGroundLevel">
      <a-radio-group v-model:value="building.isGroundLevel" button-style="solid">
       <a-radio-button :value="true">Ja</a-radio-button>
       <a-radio-button :value="false">Nee, appartement</a-radio-button>
      </a-radio-group>
     </a-form-item>
    </a-col>
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item label="Eigenaar-bewoner" name="ownerOccupied">
      <a-radio-group v-model:value="building.ownerOccupied" button-style="solid">
       <a-radio-button :value="true">Ja</a-radio-button>
       <a-radio-button :value="false">Nee</a-radio-button>
      </a-radio-group>
     </a-form-item>
    </a-col>
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item label="Lid van een VvE" name="hasVve">
      <a-radio-group v-model:value="building.hasVve" button-style="solid">
       <a-radio-button :value="true">Ja</a-radio-button>
       <a-radio-button :value="false">Nee</a-radio-button>
      </a-radio-group>
     </a-form-item>
    </a-col>
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item label="Slecht geïsoleerde bouwdelen" name="poorlyInsulatedParts">
      <a-radio-group v-model:value="building.poorlyInsulatedParts" button-style="solid">
       <a-radio-button :value="0">0</a-radio-button>
       <a-radio-button :value="1">1</a-radio-button>
       <a-radio-button :value="2">2</a-radio-button>
       <a-radio-button :value="3">3 of meer</a-radio-button>
      </a-radio-group>
     </a-form-item>
    </a-col>
    <a-col :md="12" :xs="24" class="mb-2">
     <a-form-item name="wozValue">
      <a-input v-model:value="building.wozValue" :addon-before="mark('WOZ-waarde')" addon-after="€" type="number"/>
     </a-form-item>
    </a-col>
   </a-row>
   </a-form>
  </a-col>
 </a-row>
</template>

<script>
import {computed, reactive, ref, watch} from "vue";
import {useStore} from "vuex";
import {DataService} from "../../../config/dataService/dataService";

// Velden die de BAG kan leveren; de rest vult verkoop altijd zelf in
const BAG_FIELDS = ['buildingYear', 'isGroundLevel', 'municipality'];

const emptyBuilding = () => ({
 buildingYear: null,
 isGroundLevel: null,
 municipality: null,
 municipalityCode: null,
 ownerOccupied: null,
 hasVve: null,
 poorlyInsulatedParts: null,
 wozValue: null,
 bag: null,
 bagAddress: null,
});

const filled = value => value !== null && value !== undefined && value !== '';
const requiredRule = message => [{
 required: true,
 message,
 validator: async (_rule, value) => {
  if (!filled(value)) throw new Error(message);
 },
 trigger: 'change',
}];

// Verplicht bij isolatie: zonder deze antwoorden is de subsidie niet te bepalen
const rules = {
 buildingYear: requiredRule('Vul het bouwjaar in'),
 municipality: requiredRule('Vul de gemeente in'),
 isGroundLevel: requiredRule('Kies of de woning grondgebonden is'),
 ownerOccupied: requiredRule('Kies of de klant eigenaar-bewoner is'),
 hasVve: requiredRule('Kies of de klant lid is van een VvE'),
 poorlyInsulatedParts: requiredRule('Kies het aantal slecht geïsoleerde bouwdelen'),
 wozValue: requiredRule('Vul de WOZ-waarde in'),
};

const BuildingQuestions = {
 name: 'BuildingQuestions',
 props: {
  // Alle woninggegevens verplicht (bij isolatie in het mandje)
  required: {type: Boolean, default: false},
 },
 setup(props) {
  const {state} = useStore();
  const form = ref();
  const order = computed(() => state.shop.order);
  const loading = ref(false);
  const fromBag = reactive({});
  // null = nog niet opgevraagd, 'ok', 'partial' (BAG werkt, adres deels onbekend) of 'down'
  const bagStatus = ref(null);

  const building = computed(() => {
   if (!order.value?.extraData) return null;
   if (!order.value.extraData.building) {
    order.value.extraData.building = emptyBuilding();
   }
   return order.value.extraData.building;
  });

  const address = computed(() => {
   const r = order.value?.relation;
   if (!r?.postalCode || !r?.houseNumber) return null;
   return {
    postalCode: r.postalCode,
    houseNumber: r.houseNumber,
    houseNumberAddition: r.houseNumberAddition && r.houseNumberAddition !== 'geen' ? r.houseNumberAddition : null,
   };
  });

  const showBagResult = (data) => {
   const b = building.value;
   BAG_FIELDS.forEach(field => {
    if (data[field] !== null && data[field] !== undefined && b[field] === data[field]) fromBag[field] = true;
   });
   if (!data.bagAvailable) {
    bagStatus.value = 'down';
   } else {
    bagStatus.value = BAG_FIELDS.some(field => b[field] === null || b[field] === '') ? 'partial' : 'ok';
   }
  };

  const fetchBuildingInfo = async (addr, force = false) => {
   if (!addr || !building.value) return;
   // Hetzelfde adres is al opgevraagd (bv. in de stap Isolatie-advies): de BAG niet nog eens belasten
   const key = JSON.stringify(addr);
   if (!force && building.value.bagAddress === key && building.value.bag?.bagAvailable) {
    showBagResult(building.value.bag);
    return;
   }
   loading.value = true;
   try {
    const {data} = await DataService.get('offerflow/buildinginfo', addr);
    const b = building.value;
    b.bag = data;
    b.bagAddress = key;
    b.municipalityCode = data.municipalityCode ?? b.municipalityCode;
    // Alleen lege velden vullen, zodat handmatige antwoorden blijven staan
    BAG_FIELDS.forEach(field => {
     if ((b[field] === null || b[field] === '') && data[field] !== null && data[field] !== undefined) {
      b[field] = data[field];
     }
    });
    showBagResult(data);
   } catch (e) {
    // Ook bij een fout in onze eigen API vult verkoop de velden handmatig in
    bagStatus.value = 'down';
   } finally {
    loading.value = false;
   }
  };

  const retry = () => fetchBuildingInfo(address.value, true);

  watch(address, (addr, oldAddr) => {
   if (JSON.stringify(addr) !== JSON.stringify(oldAddr)) fetchBuildingInfo(addr);
  }, {immediate: true});

  const mark = label => (props.required ? label + ' *' : label);

  // Aangeroepen door de flow voor de volgende stap; zonder verplichting altijd goed
  const validate = () => (props.required && form.value ? form.value.validate() : Promise.resolve());

  return {
   form,
   rules,
   mark,
   validate,
   building,
   fromBag,
   loading,
   bagStatus,
   retry,
  };
 },
};

export default BuildingQuestions;
</script>
